import { addDays, addHours, addMonths, startOfDay, startOfMonth, startOfWeek } from 'date-fns'

import type {
  TimeGridEvent,
  TimeGranularity,
  TimeRange,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'

const clampRangeToGranularity = (timeRange: TimeRange, granularity: TimeGranularity) => {
  if (granularity === 'day') {
    const start = startOfDay(timeRange.start)
    return {
      start,
      end: addDays(start, 1),
    }
  }

  if (granularity === 'week') {
    const start = startOfWeek(timeRange.start)
    return {
      start,
      end: addDays(start, 7),
    }
  }

  if (granularity === 'range') {
    const start = startOfWeek(timeRange.start)
    return {
      start,
      end: addDays(start, 28),
    }
  }

  const start = startOfMonth(timeRange.start)
  return {
    start,
    end: addMonths(start, 1),
  }
}

// Generates contiguous hourly slots in [start, end) range.
const createSlotsByHour = (start: Date, end: Date): TimeSlot[] => {
  const slots: TimeSlot[] = []
  let cursor = start
  let index = 0

  while (cursor < end) {
    const next = addHours(cursor, 1)
    slots.push({ index, start: cursor, end: next })
    cursor = next
    index += 1
  }

  return slots
}

// Generates contiguous daily slots in [start, end) range.
const createSlotsByDay = (start: Date, end: Date): TimeSlot[] => {
  const slots: TimeSlot[] = []
  let cursor = start
  let index = 0

  while (cursor < end) {
    const next = addDays(cursor, 1)
    slots.push({ index, start: cursor, end: next })
    cursor = next
    index += 1
  }

  return slots
}

// Normalizes the range to the granularity and builds its slots.
export const buildTimeSlots = ({
  timeRange,
  granularity,
  extraHours = 0,
}: {
  timeRange: TimeRange
  granularity: TimeGranularity
  extraHours?: number
}): { slots: TimeSlot[]; range: TimeRange } => {
  const normalizedRange = clampRangeToGranularity(timeRange, granularity)

  if (granularity === 'day') {
    const extendedEnd =
      extraHours > 0 ? addHours(normalizedRange.end, extraHours) : normalizedRange.end
    return {
      slots: createSlotsByHour(normalizedRange.start, extendedEnd),
      range: { start: normalizedRange.start, end: extendedEnd },
    }
  }

  return {
    slots: createSlotsByDay(normalizedRange.start, normalizedRange.end),
    range: normalizedRange,
  }
}

export const computeExtraHoursForTimeRange = (
  events: TimeGridEvent[],
  timeRange: TimeRange,
  granularity: TimeGranularity
) => {
  if (granularity !== 'day') return 0

  const latestEnd = events.reduce((latest, event) => {
    return event.end > latest ? event.end : latest
  }, timeRange.end)

  if (latestEnd <= timeRange.end) return 0

  const diffInMs = latestEnd.getTime() - timeRange.end.getTime()
  return Math.ceil(diffInMs / (60 * 60 * 1000))
}
